// client/src/context/WhatsAppContext.jsx
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/api';

// Create context
const WhatsAppContext = createContext();

// Custom hook to use the WhatsApp context
export const useWhatsApp = () => useContext(WhatsAppContext);

// Provider component
export const WhatsAppProvider = ({ children }) => {
  const [currentState, setCurrentState] = useState('welcome');
  const [welcomeData, setWelcomeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const socketInitialized = useRef(false);

  // Check authentication status
  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Basic token validation - you might want to decode and check expiry
        const payload = JSON.parse(atob(token.split('.')[1]));
        const isExpired = Date.now() >= payload.exp * 1000;
        setIsAuthenticated(!isExpired);
        return !isExpired;
      } catch (error) {
        console.error('Invalid token:', error);
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        return false;
      }
    }
    setIsAuthenticated(false);
    return false;
  }, []);

  // Initialize socket connection if not already done
  useEffect(() => {
    const isAuth = checkAuth();
    
    if (isAuth) {
      try {
        // Set up event listeners for future socket implementation
        // console.log('Token found, socket will be initialized in future implementation');
      } catch (error) {
        console.error('Error initializing socket:', error);
        setError('Failed to connect to chat service');
      }
    }
    
    // Load welcome message - only if authenticated OR if welcome route is public
    fetchWelcomeMessage();
    
    // Cleanup on unmount
    return () => {
      // Socket cleanup will go here
    };
  }, []);

  // Handle new message from socket (placeholder for future implementation)
  const handleNewMessage = useCallback((message) => {
    if (!message || !message.phoneNumber) return;
    
    // Add to chat history if it matches current selected contact
    if (selectedContact && message.phoneNumber === selectedContact.phoneNumber) {
      addMessageToChat(message.content, message.sender, message.timestamp);
    }
    
    // Update contact's last message and status
    setContacts(prevContacts => {
      return prevContacts.map(contact => {
        if (contact.phoneNumber === message.phoneNumber) {
          return {
            ...contact,
            lastMessage: message.content,
            timestamp: message.timestamp || new Date().toISOString(),
            unread: selectedContact?.phoneNumber !== message.phoneNumber
          };
        }
        return contact;
      });
    });
  }, [selectedContact]);

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Authentication required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.getContacts();
      if (response && response.data && response.data.data) {
        setContacts(response.data.data);
      } else {
        setContacts([]);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        setIsAuthenticated(false);
        localStorage.removeItem('token');
      } else {
        setError('Failed to fetch contacts');
      }
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch messages for a contact
  const fetchMessages = useCallback(async (contactPhoneNumber) => {
    if (!contactPhoneNumber) return;
    
    if (!isAuthenticated) {
      setError('Authentication required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.getMessages(contactPhoneNumber);
      if (response && response.data && response.data.data) {
        setChatHistory(response.data.data);
      } else {
        setChatHistory([]);
      }
      
      // Update contacts to reflect read status
      setContacts(prevContacts => {
        return prevContacts.map(contact => {
          if (contact.phoneNumber === contactPhoneNumber) {
            return { ...contact, unread: false };
          }
          return contact;
        });
      });
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        setIsAuthenticated(false);
        localStorage.removeItem('token');
      } else {
        setError('Failed to fetch messages');
      }
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch welcome message
  const fetchWelcomeMessage = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.getWelcomeMessage();
      if (response && response.data && response.data.data) {
        setWelcomeData(response.data.data);
      } else {
        setWelcomeData({
          greeting: "",
          options: [],
          help: ""
        });
      }
    } catch (err) {
      // Handle 401 errors gracefully for welcome message
      if (err.response?.status === 401) {
        // console.log('Welcome message requires authentication - using fallback data');
      } else {
        setError('Failed to fetch welcome message');
        console.error('Error fetching welcome message:', err);
      }
      
      // Set empty welcome data on error
      setWelcomeData({
        greeting: "",
        options: [],
        help: ""
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Send welcome message to a phone number
  const sendWelcomeMessage = async (number) => {
    if (!isAuthenticated) {
      setError('Authentication required');
      return null;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.sendWelcomeMessage(number);
      setPhoneNumber(number);
      
      // Add system message to chat history
      addMessageToChat('Welcome to brand!! How can we help?', 'system');
      
      return response?.data || { success: true };
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        setIsAuthenticated(false);
        localStorage.removeItem('token');
      } else {
        setError(err.response?.data?.message || 'Failed to send welcome message');
      }
      console.error('Error sending welcome message:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Handle option selection
  const selectOption = async (option) => {
    if (!phoneNumber) {
      setError('Phone number not set');
      return null;
    }
    
    if (!isAuthenticated) {
      setError('Authentication required');
      return null;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Add user message to chat history
      addMessageToChat(`Selected option: ${option}`, 'user');
      
      const response = await apiService.selectOption(phoneNumber, option);
      if (response && response.data) {
        setCurrentState(response.data.nextState || currentState);
        return response.data;
      }
      
      return null;
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        setIsAuthenticated(false);
        localStorage.removeItem('token');
      } else {
        setError(err.response?.data?.message || 'Failed to process option');
      }
      console.error('Error selecting option:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Send a message
  const sendMessage = async (message, recipientPhoneNumber = phoneNumber) => {
    if (!recipientPhoneNumber) {
      setError('Recipient phone number not set');
      return null;
    }
    
    if (!isAuthenticated) {
      setError('Authentication required');
      return null;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Add to chat history immediately for UI responsiveness
      const newMessage = addMessageToChat(message, 'agent');
      
      // Send message via API
      const response = await apiService.sendMessage(recipientPhoneNumber, message);
      
      // Update message status based on API response
      if (response && response.data && response.data.success) {
        setChatHistory(prev => 
          prev.map(msg => 
            msg.id === newMessage.id ? { ...msg, status: 'sent' } : msg
          )
        );
      }
      
      return response?.data;
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        setIsAuthenticated(false);
        localStorage.removeItem('token');
      } else {
        setError(err.response?.data?.message || 'Failed to send message');
      }
      console.error('Error sending message:', err);
      
      // Update message status to failed
      setChatHistory(prev => 
        prev.map(msg => 
          msg.content === message && msg.sender === 'agent' ? { ...msg, status: 'failed' } : msg
        )
      );
      
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Add message to chat history
  const addMessageToChat = (content, sender, timestamp = new Date().toISOString()) => {
    const newMessage = {
      id: Date.now().toString(),
      content,
      sender,
      timestamp,
      status: sender === 'user' ? 'received' : 'pending'
    };
    
    setChatHistory(prev => [...prev, newMessage]);
    return newMessage;
  };

  // Select a contact
  const selectContact = useCallback((contact) => {
    setSelectedContact(contact);
    setPhoneNumber(contact.phoneNumber);
    fetchMessages(contact.phoneNumber);
  }, [fetchMessages]);

  // Reset chat
  const resetChat = () => {
    setCurrentState('welcome');
    setChatHistory([]);
    setPhoneNumber('');
    setSelectedContact(null);
  };

  // Context value
  const value = {
    currentState,
    welcomeData,
    loading,
    error,
    phoneNumber,
    chatHistory,
    contacts,
    selectedContact,
    isAuthenticated,
    sendWelcomeMessage,
    selectOption,
    sendMessage,
    addMessageToChat,
    fetchContacts,
    fetchMessages,
    selectContact,
    resetChat,
    setPhoneNumber,
    checkAuth
  };

  return (
    <WhatsAppContext.Provider value={value}>
      {children}
    </WhatsAppContext.Provider>
  );
};

export default WhatsAppContext;